import React, { useState, useEffect } from 'react';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faPencilAlt,
    faBell,
    faDownload,
    faEye,
    faStar,
    faMobileAlt,
    faCheckCircle,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import DeleteAlert from '../Shared/DeleteAlert';
import MiniLoader from '../Shared/MiniLoader';

const AppViewer = () => {
    const navigate = useNavigate();
    const [appViews, setAppViews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [notifyingAppId, setNotifyingAppId] = useState(null);

    const fetchAppViews = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                PageSize: pageSize,
                PageNumber: page,
                // IsActive: true, // As per user requirements
            };
            const response = await ApiServices.get('AppView/getAll', params);
            if (response && response.data) {
                setAppViews(response.data.items || []);
                setTotalItems(response.data.total_records || 0);
            }
        } catch (error) {
            toastHelper.error('فشل في جلب بيانات العرض');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAppViews(pageNumber);
    }, [pageNumber]);

    const handleNotifyUpdate = async (app) => {
        if (notifyingAppId === app.id) return;
        if (!window.confirm(`هل أنت متأكد من إرسال إشعار التحديث للإصدار ${app.version}؟`)) return;

        try {
            setNotifyingAppId(app.id);
            await ApiServices.put('AppView/send/emailUpdate');
            toastHelper.success('تم إرسال إشعارات التحديث بنجاح');
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء إرسال الإشعار');
        } finally {
            setNotifyingAppId(null);
        }
    };

    const columns = [
        {
            key: 'app_icon',
            label: 'الأيقونة',
            width: '80px',
            render: (val) => (
                <div className="app-icon-wrapper">
                    <img src={getFullFileUrl(val)} alt="App Icon" className="app-icon-img" />
                </div>
            )
        },
        {
            key: 'version',
            label: 'الإصدار',
            sortable: true,
            render: (val, row) => (
                <div className="version-cell">
                    <span className="version-txt">{val}</span>
                    <span className={`priority-badge ${row.priority?.toLowerCase()}`}>
                        {row.priority === 'High' ? 'عالي' : 'عادي'}
                    </span>
                </div>
            )
        },
        {
            key: 'description',
            label: 'الوصف',
            render: (val) => <p className="desc-truncate" title={val}>{val}</p>
        },
        {
            key: 'stats',
            label: 'الإحصائيات',
            render: (_, row) => (
                <div className="stats-grid">
                    <div className="stat-item" title="التحميلات">
                        <FontAwesomeIcon icon={faDownload} />
                        <span>{row.downloads || 0}</span>
                    </div>
                    <div className="stat-item" title="التنزيلات">
                        <FontAwesomeIcon icon={faEye} />
                        <span>{row.views || 0}</span>
                    </div>
                    <div className="stat-item" title="التقييم">
                        <FontAwesomeIcon icon={faStar} />
                        <span>{row.rate || 0}</span>
                    </div>
                </div>
            )
        },
        {
            key: 'screenshots',
            label: 'لقطات الشاشة',
            render: (_, row) => (
                <div className="screenshots-preview">
                    {[1, 2, 3].map(i => row[`sreenshot${i}`] && (
                        <img
                            key={i}
                            src={getFullFileUrl(row[`sreenshot${i}`])}
                            alt={`SS ${i}`}
                            className="ss-mini"
                        />
                    ))}
                    {row.sreenshot4 && <span className="ss-more">+</span>}
                </div>
            )
        }
    ];

    const actions = (row) => [
        {
            label: 'تعديل',
            icon: <FontAwesomeIcon icon={faPencilAlt} />,
            onClick: (row) => navigate('/admin/app-viewer/edit', { state: { appView: row } }),
            className: 'edit-btn'
        },
        {
            label: notifyingAppId === row.id ? 'جاري الإخطار...' : 'إخطار بالتحديث',
            icon: notifyingAppId === row.id
                ? <MiniLoader size={14} color="#6366f1" />
                : <FontAwesomeIcon icon={faBell} />,
            onClick: (row) => handleNotifyUpdate(row),
            className: 'notify-btn',
            disabled: notifyingAppId === row.id
        }
    ];

    return (
        <div className="app-viewer-container">
            <div className="page-header">
                <div className="header-info">
                    <h2>عروض التطبيق (App Viewer)</h2>
                    <p>إدارة إصدارات التطبيق، الأيقونات، ولقطات الشاشة المعروضة ({totalItems})</p>
                </div>
                <button className="add-app-btn" onClick={() => navigate('/admin/app-viewer/add')}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>إضافة عرض جديد</span>
                </button>
            </div>

            <SoftGrid
                columns={columns}
                data={appViews}
                isLoading={loading}
                actions={actions}
                pagination={{
                    currentPage: pageNumber,
                    totalItems: totalItems,
                    pageSize: pageSize,
                    onPageChange: (p) => setPageNumber(p)
                }}
                searchPlaceholder="ابحث في الإصدارات..."
            />
            <style>{`
                .app-viewer-container {
                    padding: 20px;
                    direction: rtl;
                }
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                }
                .header-info h2 {
                    margin: 0;
                    color: #1e293b;
                    font-size: 1.5rem;
                    font-weight: 800;
                }
                .header-info p {
                    margin: 4px 0 0;
                    color: #64748b;
                }
                .add-app-btn {
                    background: #6366f1;
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.4);
                }
                .add-app-btn:hover {
                    background: #4f46e5;
                    transform: translateY(-1px);
                }

                .app-icon-img {
                    width: 50px;
                    height: 50px;
                    border-radius: 10px;
                    object-fit: cover;
                    border: 2px solid #f1f5f9;
                }
                
                .version-cell {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .version-txt {
                    font-weight: 800;
                    color: #1e293b;
                }
                .priority-badge {
                    font-size: 0.7rem;
                    padding: 2px 8px;
                    border-radius: 6px;
                    width: fit-content;
                    font-weight: 700;
                }
                .priority-badge.high { background: #fee2e2; color: #ef4444; }
                .priority-badge.normal { background: #dcfce7; color: #16a34a; }

                .desc-truncate {
                    max-width: 300px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    color: #64748b;
                    margin: 0;
                }

                .stats-grid {
                    display: flex;
                    gap: 12px;
                }
                .stat-item {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    color: #64748b;
                    font-size: 0.85rem;
                }
                .stat-item span { font-weight: 700; }

                .screenshots-preview {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                }
                .ss-mini {
                    width: 30px;
                    height: 50px;
                    object-fit: cover;
                    border-radius: 4px;
                    border: 1px solid #e2e8f0;
                }
                .ss-more {
                    font-size: 0.8rem;
                    color: #94a3b8;
                    font-weight: 700;
                }

                .notify-btn { color: #6366f1 !important; }
            `}</style>
        </div>
    );
};

export default AppViewer;
